import React, { useEffect, useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { baseType, formatDuration, LIMITS } from '../../../utils/attachmentRules';

const EXT = { 'audio/webm': 'webm', 'audio/mp4': 'm4a', 'audio/ogg': 'ogg' };
const btn = 'inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold';

export const AudioRecorder = ({ onRecorded, onClose }) => {
  const [state, setState] = useState('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const secondsRef = useRef(0);
  const discardRef = useRef(false);

  const supported =
    typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

  const stopTimer = () => clearInterval(timerRef.current);
  const stopStream = () => streamRef.current?.getTracks().forEach((t) => t.stop());

  useEffect(() => () => {
    stopTimer();
    discardRef.current = true;
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    stopStream();
  }, []);

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
      if (secondsRef.current >= LIMITS.audioSeconds) recRef.current?.stop();
    }, 1000);
  };

  const start = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) =>
        window.MediaRecorder.isTypeSupported(t)
      );
      const rec = new window.MediaRecorder(stream, { audioBitsPerSecond: 64000, ...(mimeType ? { mimeType } : {}) });
      chunksRef.current = [];
      discardRef.current = false;
      secondsRef.current = 0;
      setSeconds(0);
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stopTimer();
        stopStream();
        const mime = baseType(rec.mimeType) || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        const duration = secondsRef.current;
        setState('idle');
        if (!discardRef.current && blob.size > 0) {
          const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
          onRecorded({ blob, mime, duration, name: `Voice note ${stamp}.${EXT[mime] || 'webm'}` });
          onClose();
        }
      };
      recRef.current = rec;
      rec.start();
      startTimer();
      setState('recording');
    } catch (err) {
      stopStream();
      setError(
        err?.name === 'NotAllowedError'
          ? 'Microphone access was blocked. Allow it in your browser settings and try again.'
          : err?.name === 'NotFoundError'
            ? 'No microphone was found on this device.'
            : 'Could not start recording.'
      );
    }
  };

  const togglePause = () => {
    const rec = recRef.current;
    if (state === 'recording') {
      rec.pause();
      stopTimer();
      setState('paused');
    } else {
      rec.resume();
      startTimer();
      setState('recording');
    }
  };

  const cancel = () => {
    discardRef.current = true;
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    onClose();
  };

  if (!supported) {
    return (
      <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
        Recording is not supported in this browser. You can still attach an existing audio file.
        <button type="button" onClick={onClose} className="ml-2 font-semibold underline">Close</button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {state === 'idle' ? (
        <div className="flex gap-2">
          <button type="button" onClick={start} className={`${btn} bg-slate-900 text-white`}>
            <SafeIcon icon={FiIcons.FiMic} /> Start recording
          </button>
          <button type="button" onClick={onClose} className={`${btn} text-slate-600`}>Cancel</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-2 text-sm font-bold tabular-nums text-slate-900">
            <span className={`h-2.5 w-2.5 rounded-full bg-red-500 ${state === 'recording' ? 'animate-pulse' : ''}`} />
            {formatDuration(seconds)}
          </span>
          <button type="button" onClick={togglePause} className={`${btn} bg-white border border-slate-200`}>
            <SafeIcon icon={state === 'recording' ? FiIcons.FiPause : FiIcons.FiPlay} />
            {state === 'recording' ? 'Pause' : 'Resume'}
          </button>
          <button type="button" onClick={() => recRef.current.stop()} className={`${btn} bg-slate-900 text-white`}>
            <SafeIcon icon={FiIcons.FiSquare} /> Stop and keep
          </button>
          <button type="button" onClick={cancel} className={`${btn} text-slate-600`}>Discard</button>
        </div>
      )}
    </div>
  );
};